import React from "react";
import axios from "axios";
import { MapPin, Loader2 } from "lucide-react";
import { API } from "@/config";

const newSession = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

/**
 * AddressAutocomplete — text input backed by Google Places suggestions
 * (proxied through /api/places so the key stays server-side). Selecting a
 * suggestion resolves the full address (street, city, state, ZIP + lat/lng)
 * and calls `onSelect(details)`; free typing still works as a plain input.
 */
export const AddressAutocomplete = ({ value, onChange, onSelect, placeholder, name, testid, className = "input" }) => {
  const [suggestions, setSuggestions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(-1);
  const [loading, setLoading] = React.useState(false);
  const [resolving, setResolving] = React.useState(false);
  const sessionRef = React.useRef(newSession());
  const timerRef = React.useRef(null);
  const reqRef = React.useRef(0);
  const wrapRef = React.useRef(null);

  const fetchSuggestions = React.useCallback(async (text) => {
    const id = ++reqRef.current;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/places/autocomplete`, { params: { input: text, session: sessionRef.current } });
      if (id !== reqRef.current) return;
      setSuggestions(res.data.suggestions || []);
      setOpen((res.data.suggestions || []).length > 0);
      setActive(-1);
    } catch {
      if (id === reqRef.current) { setSuggestions([]); setOpen(false); }
    } finally {
      if (id === reqRef.current) setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const text = e.target.value;
    onChange(text);
    clearTimeout(timerRef.current);
    if (text.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    timerRef.current = setTimeout(() => fetchSuggestions(text), 250);
  };

  const pick = async (s) => {
    clearTimeout(timerRef.current);
    reqRef.current++;
    setOpen(false); setSuggestions([]);
    onChange(s.description);
    setResolving(true);
    try {
      const res = await axios.get(`${API}/places/details/${encodeURIComponent(s.place_id)}`, { params: { session: sessionRef.current } });
      sessionRef.current = newSession();
      onChange(res.data.formatted || s.description);
      onSelect?.(res.data);
    } catch {
      onSelect?.(null);
    } finally { setResolving(false); }
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(suggestions[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  React.useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => { document.removeEventListener("mousedown", onDoc); clearTimeout(timerRef.current); };
  }, []);

  return (
    <div ref={wrapRef} className="relative flex-1 min-w-0" data-testid={`${testid}-wrap`}>
      <input
        className={className} type="text" name={name} autoComplete="off" spellCheck={false}
        placeholder={placeholder} value={value}
        onChange={handleChange} onKeyDown={onKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        role="combobox" aria-expanded={open} aria-autocomplete="list"
        data-testid={testid}
      />
      {(loading || resolving) && (
        <Loader2 className="w-4 h-4 animate-spin text-[#FFD166] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" data-testid={`${testid}-loading`} />
      )}
      {open && (
        <ul
          role="listbox"
          className="absolute z-50 left-0 right-0 mt-1 rounded-xl border border-[#FFD166]/25 bg-[#0B1B2B]/95 backdrop-blur-md shadow-2xl overflow-hidden"
          data-testid={`${testid}-suggestions`}
        >
          {suggestions.map((s, i) => (
            <li
              key={s.place_id}
              role="option" aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setActive(i)}
              className={`flex items-start gap-2.5 px-3 py-2.5 cursor-pointer text-sm transition-colors ${i === active ? "bg-[#FFD166]/15" : "hover:bg-[#FFD166]/10"}`}
              data-testid={`${testid}-suggestion-${i}`}
            >
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#FFD166]" />
              <span className="min-w-0">
                <span className="block text-[#F5F0E6] truncate">{s.main_text}</span>
                <span className="block text-xs text-[#B9C8D5] truncate">{s.secondary_text}</span>
              </span>
            </li>
          ))}
          <li className="px-3 py-1.5 text-[10px] text-[#B9C8D5]/70 text-right border-t border-[#FFD166]/10">Powered by Google</li>
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
